/**
 * Bulk quiz question import modal.
 * Provides JSON template generation, client-side validation, and batch import workflows.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Space,
  Tag,
  Tooltip,
  Collapse,
  Typography,
  Alert,
  Input,
  message,
  Badge,
  Divider,
} from 'antd';
import {
  CopyOutlined,
  ClearOutlined,
  FormatPainterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  BulbOutlined,
  CheckOutlined,
  AimOutlined,
} from '@ant-design/icons';
import Editor, { loader } from '@monaco-editor/react';
import { getBulkImportTemplate, bulkAddQuestions } from '../../services/quizCollectionsService';
import styles from './Quiz.module.css';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// Field constraints (must match backend)
const CONSTRAINTS = {
  codeMaxLength: 50,
  bodyMaxLength: 5000,
  topicMaxLength: 100,
  subtopicMaxLength: 100,
  lawMaxLength: 255,
  versionMaxLength: 50,
  choiceTextMaxLength: 1000,
  minChoices: 2,
};

const MONACO_INIT_TIMEOUT_MS = 10000;
const MONACO_MOUNT_TIMEOUT_MS = 10000;

/**
 * Build a sample bulk-import payload with realistic quiz data.
 * Uses PascalCase keys to match the expected client-side import format.
 *
 * @param {string[]} availableLevels - Difficulty labels available for the current collection.
 * @param {string[]} availableTopics - Topic labels available for the current collection.
 * @returns {string} Pretty-printed JSON template ready for editing.
 */
const generateTemplateJson = (availableLevels, availableTopics) => {
  const levels = availableLevels.length > 0 ? availableLevels : ['Easy', 'Medium', 'Hard'];
  const topics = availableTopics.length > 0 ? availableTopics : [];

  const withWeights = (weights) => (weights.length > 0 ? { Weights: weights } : {});

  const template = [
    {
      Code: "HVAC-001",
      Body: "What is the primary function of a compressor in a refrigeration system?",
      LevelOfDifficulty: levels[0] || "Easy",
      Topic: "HVAC Fundamentals",
      Subtopic: "Refrigeration Cycle",
      Law: "EN 378-1:2016",
      Version: "1.0",
      ...withWeights(
        topics.length >= 2
          ? [
              { TopicName: topics[0], Weight: 0.8 },
              { TopicName: topics[1], Weight: 0.4 }
            ]
          : topics.length === 1
            ? [{ TopicName: topics[0], Weight: 0.9 }]
            : []
      ),
      Choices: [
        { Text: "To compress refrigerant gas and increase its pressure and temperature", IsCorrect: true },
        { Text: "To cool the surrounding air directly", IsCorrect: false },
        { Text: "To filter impurities from the refrigerant", IsCorrect: false },
        { Text: "To reduce system noise levels", IsCorrect: false }
      ]
    },
    {
      Code: "HVAC-002",
      Body: "Which of the following are common types of heat exchangers used in HVAC systems? (Select all that apply)",
      LevelOfDifficulty: levels[1] || levels[0] || "Medium",
      Topic: "Heat Transfer",
      Subtopic: "Equipment Types",
      Law: "ASHRAE Standard 90.1",
      Version: "2.1",
      ...withWeights(
        topics.length >= 3
          ? [
              { TopicName: topics[0], Weight: 0.6 },
              { TopicName: topics[1], Weight: 0.9 },
              { TopicName: topics[2], Weight: 0.3 }
            ]
          : topics.length >= 2
            ? [
                { TopicName: topics[0], Weight: 0.7 },
                { TopicName: topics[1], Weight: 0.5 }
              ]
            : []
      ),
      Choices: [
        { Text: "Shell and tube heat exchanger", IsCorrect: true },
        { Text: "Plate heat exchanger", IsCorrect: true },
        { Text: "Rotary compressor", IsCorrect: false },
        { Text: "Finned tube heat exchanger", IsCorrect: true },
        { Text: "Expansion valve", IsCorrect: false }
      ]
    },
    {
      Code: null,
      Body: "What is the ideal superheat range for most refrigeration systems?",
      LevelOfDifficulty: levels[2] || levels[0] || "Hard",
      Topic: "System Diagnostics",
      Subtopic: "Superheat & Subcooling",
      Law: "EPA Section 608",
      Version: "1.5",
      ...withWeights(
        topics.length >= 3
          ? [
              { TopicName: topics[0], Weight: 0.2 },
              { TopicName: topics[1], Weight: 0.8 },
              { TopicName: topics[2], Weight: 0.95 }
            ]
          : []
      ),
      Choices: [
        { Text: "5-15°F (3-8°C)", IsCorrect: false },
        { Text: "10-20°F (5.5-11°C)", IsCorrect: true },
        { Text: "25-35°F (14-19°C)", IsCorrect: false },
        { Text: "0-5°F (0-3°C)", IsCorrect: false }
      ]
    }
  ];

  return JSON.stringify(template, null, 2);
};

/**
 * Find the line range for a specific question object inside the raw JSON document.
 * Used to focus the Monaco editor on the selected validation result.
 *
 * @param {string} jsonString - Raw JSON source from the editor.
 * @param {number} questionIndex - Zero-based question index within the root array.
 * @returns {{startLine: number, endLine: number} | null} Line range for the question or null when unresolved.
 */
const findQuestionLineRange = (jsonString, questionIndex) => {
  try {
    let inString = false;
    let escapeNext = false;
    let inRootArray = false;
    let objectDepth = 0;
    let arrayDepth = 0;
    let currentQuestionIndex = -1;
    let questionStartLine = -1;
    let lineNumber = 1;

    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString[i];

      if (char === '\n') {
        lineNumber++;
        continue;
      }

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        escapeNext = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (inString) continue;

      if (char === '[') {
        if (arrayDepth === 0 && objectDepth === 0) {
          inRootArray = true;
        }
        arrayDepth++;
        continue;
      }

      if (char === ']') {
        arrayDepth--;
        continue;
      }

      if (char === '{') {
        // Only count as question if we're in root array and at object depth 0
        if (inRootArray && objectDepth === 0) {
          currentQuestionIndex++;
          if (currentQuestionIndex === questionIndex) {
            questionStartLine = lineNumber;
          }
        }
        objectDepth++;
        continue;
      }

      if (char === '}') {
        objectDepth--;
        // Check if we just closed a question object
        if (inRootArray && objectDepth === 0 && currentQuestionIndex === questionIndex) {
          return { startLine: questionStartLine, endLine: lineNumber };
        }
        continue;
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Validate a single imported question before it is submitted to the API.
 * Mirrors the backend rules closely so users get fast feedback in the modal.
 *
 * @param {object} question - Candidate question object from the import JSON.
 * @param {number} index - Zero-based question index in the import batch.
 * @param {Set<string>} existingCodes - Existing question codes already present in the collection.
 * @param {Set<string>} batchCodes - Question codes encountered earlier in the current batch.
 * @param {string[]} availableLevels - Valid difficulty names.
 * @param {string[]} availableTopics - Valid topic names.
 * @returns {string[]} Validation error messages for the question.
 */
const validateQuestion = (question, index, existingCodes, batchCodes, availableLevels, availableTopics) => {
  const errors = [];
  const levelNames = availableLevels.map(l => l.toLowerCase());
  const topicNames = availableTopics.map(t => t.toLowerCase());

  // Body validation
  if (!question.Body || typeof question.Body !== 'string' || !question.Body.trim()) {
    errors.push('Body is required');
  } else if (question.Body.length > CONSTRAINTS.bodyMaxLength) {
    errors.push(`Body exceeds ${CONSTRAINTS.bodyMaxLength} chars (got ${question.Body.length})`);
  }

  // Code validation
  const code = question.Code?.trim();
  if (code) {
    if (code.length > CONSTRAINTS.codeMaxLength) {
      errors.push(`Code exceeds ${CONSTRAINTS.codeMaxLength} chars (got ${code.length})`);
    }
    if (existingCodes.has(code.toLowerCase())) {
      errors.push(`Code "${code}" already exists in collection`);
    }
    if (batchCodes.has(code.toLowerCase())) {
      errors.push(`Duplicate code "${code}" in batch`);
    }
  }

  // Optional text field validation
  if (question.Topic && question.Topic.length > CONSTRAINTS.topicMaxLength) {
    errors.push(`Topic exceeds ${CONSTRAINTS.topicMaxLength} chars`);
  }
  if (question.Subtopic && question.Subtopic.length > CONSTRAINTS.subtopicMaxLength) {
    errors.push(`Subtopic exceeds ${CONSTRAINTS.subtopicMaxLength} chars`);
  }
  if (question.Law && question.Law.length > CONSTRAINTS.lawMaxLength) {
    errors.push(`Law exceeds ${CONSTRAINTS.lawMaxLength} chars`);
  }
  if (question.Version && question.Version.length > CONSTRAINTS.versionMaxLength) {
    errors.push(`Version exceeds ${CONSTRAINTS.versionMaxLength} chars`);
  }

  // Level of difficulty validation
  if (question.LevelOfDifficulty) {
    const levelKey = question.LevelOfDifficulty.trim().toLowerCase();
    if (!levelNames.includes(levelKey)) {
      errors.push(`Unknown difficulty: "${question.LevelOfDifficulty}"`);
    }
  }

  // Choices validation
  if (!question.Choices || !Array.isArray(question.Choices)) {
    errors.push('Choices array is required');
  } else {
    if (question.Choices.length < CONSTRAINTS.minChoices) {
      errors.push(`At least ${CONSTRAINTS.minChoices} choices required (got ${question.Choices.length})`);
    }
    if (!question.Choices.some(c => c.IsCorrect)) {
      errors.push('At least 1 correct choice required');
    }
    question.Choices.forEach((choice, i) => {
      if (!choice.Text || !choice.Text.trim()) {
        errors.push(`Choice ${i + 1}: Text required`);
      } else if (choice.Text.length > CONSTRAINTS.choiceTextMaxLength) {
        errors.push(`Choice ${i + 1}: Text exceeds ${CONSTRAINTS.choiceTextMaxLength} chars`);
      }
    });
  }

  // Weights validation (optional)
  if (question.Weights && Array.isArray(question.Weights)) {
    question.Weights.forEach(w => {
      if (w.TopicName) {
        const topicKey = w.TopicName.trim().toLowerCase();
        if (!topicNames.includes(topicKey)) {
          errors.push(`Unknown weight topic: "${w.TopicName}"`);
        }
      }
      if (typeof w.Weight === 'number' && (w.Weight < 0 || w.Weight > 1)) {
        errors.push(`Weight for "${w.TopicName}" must be 0-1`);
      }
    });
  }

  return errors;
};

/**
 * Parse the editor JSON into a structured result object.
 * Captures parser failures and attempts to recover a helpful line number when possible.
 *
 * @param {string} jsonString - Raw JSON entered by the user.
 * @returns {{success: boolean, error: string | null, data: any[] | null, lineNumber: number | null}} Parse result.
 */
const parseJson = (jsonString) => {
  if (!jsonString || !jsonString.trim()) {
    return { success: false, error: 'Empty input', data: null, lineNumber: null };
  }

  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data)) {
      return { success: false, error: 'Root must be an array of questions', data: null, lineNumber: null };
    }
    return { success: true, error: null, data, lineNumber: null };
  } catch (e) {
    // Try to extract line number from error
    const match = e.message.match(/position (\d+)/);
    let lineNumber = null;
    if (match) {
      const position = parseInt(match[1], 10);
      const lines = jsonString.substring(0, position).split('\n');
      lineNumber = lines.length;
    }
    return { success: false, error: e.message, data: null, lineNumber };
  }
};

/**
 * Modal component used to import multiple quiz questions from JSON.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {number} props.collectionId - Target quiz collection identifier.
 * @param {string[]} [props.existingQuestionCodes=[]] - Existing codes to validate uniqueness against.
 * @param {Array} [props.levelsOfDifficulty=[]] - Available difficulty options for validation/template generation.
 * @param {Array} [props.topics=[]] - Available topic options for validation/template generation.
 * @param {Function} props.onClose - Invoked when the modal should close.
 * @param {Function} props.onSuccess - Invoked after a successful batch import.
 * @returns {JSX.Element} Bulk import modal.
 */
const BulkImportModal = ({
  open,
  collectionId,
  existingQuestionCodes = [],
  levelsOfDifficulty = [],
  topics = [],
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  
  // State
  const [jsonValue, setJsonValue] = useState('');
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [template, setTemplate] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [editorLoadState, setEditorLoadState] = useState('idle');
  const [editorRenderKey, setEditorRenderKey] = useState(0);

  const resetEditorState = useCallback(() => {
    editorRef.current = null;
    setEditorLoadState('idle');
    setEditorRenderKey((currentKey) => currentKey + 1);
  }, []);

  // Computed values
  const existingCodesSet = useMemo(
    () => new Set(existingQuestionCodes.map(c => c.toLowerCase())),
    [existingQuestionCodes]
  );

  const availableLevelNames = useMemo(
    () => template?.availableLevelsOfDifficulty || levelsOfDifficulty.map(l => l.name),
    [template, levelsOfDifficulty]
  );

  const availableTopicNames = useMemo(
    () => template?.availableTopics || topics.map(t => t.name),
    [template, topics]
  );

  const validCount = useMemo(
    () => validationResults.filter(v => v.valid).length,
    [validationResults]
  );

  const errorCount = useMemo(
    () => validationResults.filter(v => !v.valid).length,
    [validationResults]
  );

  const canImport = parsedQuestions.length > 0 && errorCount === 0 && !parseError;

  // Parse and validate on JSON change
  useEffect(() => {
    const result = parseJson(jsonValue);
    
    if (!result.success) {
      setParseError(result.error);
      setParsedQuestions([]);
      setValidationResults([]);
      return;
    }

    setParseError(null);
    setParsedQuestions(result.data);

    // Validate each question
    const batchCodes = new Set();
    const results = result.data.map((question, index) => {
      const code = question.Code?.trim()?.toLowerCase();
      const errors = validateQuestion(
        question,
        index,
        existingCodesSet,
        batchCodes,
        availableLevelNames,
        availableTopicNames
      );
      
      // Track code in batch (only if not empty)
      if (code) {
        batchCodes.add(code);
      }

      return {
        index,
        code: question.Code || null,
        body: question.Body || '',
        levelOfDifficulty: question.LevelOfDifficulty || null,
        topic: question.Topic || null,
        subtopic: question.Subtopic || null,
        law: question.Law || null,
        version: question.Version || null,
        choiceCount: question.Choices?.length || 0,
        correctCount: question.Choices?.filter(c => c.IsCorrect).length || 0,
        weightCount: question.Weights?.length || 0,
        errors,
        valid: errors.length === 0,
      };
    });

    setValidationResults(results);
  }, [jsonValue, existingCodesSet, availableLevelNames, availableTopicNames]);

  const loadTemplate = useCallback(async () => {
    setLoadingTemplate(true);
    try {
      const data = await getBulkImportTemplate(collectionId);
      setTemplate(data);
    } catch (error) {
      console.error('Failed to load template:', error);
      message.error(t('quiz.bulkImport.templateError', 'Failed to load template'));
    } finally {
      setLoadingTemplate(false);
    }
  }, [collectionId, t]);

  // Load template on open
  useEffect(() => {
    if (open && collectionId && !template) {
      loadTemplate();
    }
  }, [open, collectionId, template, loadTemplate]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setJsonValue('');
      setParsedQuestions([]);
      setParseError(null);
      setValidationResults([]);
      setImporting(false);
      setSelectedQuestionIndex(null);
      resetEditorState();
    }
  }, [open, resetEditorState]);

  useEffect(() => {
    let isActive = true;
    let timeoutId = null;

    if (!open || editorLoadState !== 'loading') {
      return undefined;
    }

    const initPromise = import('monaco-editor').then((monacoModule) => {
      const monaco = monacoModule.default ?? monacoModule;
      loader.config({ monaco });
      return loader.init();
    });

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Monaco initialization timed out after ${MONACO_INIT_TIMEOUT_MS}ms`));
      }, MONACO_INIT_TIMEOUT_MS);
    });

    Promise.race([initPromise, timeoutPromise])
      .then(() => {
        if (isActive) {
          setEditorLoadState('ready');
        }
      })
      .catch((error) => {
        console.error('Monaco initialization failed; using textarea fallback.', error);
        if (isActive) {
          setEditorLoadState('failed');
        }
      })
      .finally(() => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      });

    return () => {
      isActive = false;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, open]);

  useEffect(() => {
    let timeoutId = null;

    if (!open || editorLoadState !== 'ready' || editorRef.current) {
      return undefined;
    }

    timeoutId = window.setTimeout(() => {
      if (!editorRef.current) {
        console.error('Monaco editor mount timed out; using textarea fallback.');
        setEditorLoadState('failed');
      }
    }, MONACO_MOUNT_TIMEOUT_MS);

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, editorRenderKey, open]);

  const handleRetryEditor = useCallback(() => {
    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, []);

  const handleEnableAdvancedEditor = useCallback(() => {
    if (editorLoadState === 'loading') {
      return;
    }

    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, [editorLoadState]);

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
  };

  const renderEditor = () => {
    if (editorLoadState === 'failed') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('quiz.bulkImport.editorFallbackTitle', 'Advanced editor unavailable')}
            description={t(
              'quiz.bulkImport.editorFallbackDescription',
              'The syntax-highlighting editor could not be loaded in this environment. You can still paste and import JSON using the plain text editor below.'
            )}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryEditor}>
                {t('quiz.bulkImport.retryEditor', 'Retry editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            style={{ flex: 1, resize: 'none', fontFamily: 'Consolas, Monaco, monospace' }}
          />
        </div>
      );
    }

    if (editorLoadState !== 'ready') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={
              editorLoadState === 'loading'
                ? t('quiz.bulkImport.loadingEditorTitle', 'Loading advanced editor')
                : t('quiz.bulkImport.basicEditorTitle', 'Plain text editor')
            }
            description={t(
              editorLoadState === 'loading'
                ? 'quiz.bulkImport.loadingEditorDescription'
                : 'quiz.bulkImport.basicEditorDescription',
              editorLoadState === 'loading'
                ? 'You can continue editing in the plain text editor while the advanced editor loads.'
                : 'The plain text editor is ready. Start the advanced editor only if you want syntax highlighting and Monaco features.'
            )}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleEnableAdvancedEditor}
                loading={editorLoadState === 'loading'}
              >
                {editorLoadState === 'loading'
                  ? t('quiz.bulkImport.loadingEditorButton', 'Loading...')
                  : t('quiz.bulkImport.enableEditor', 'Use advanced editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            style={{ flex: 1, resize: 'none', fontFamily: 'Consolas, Monaco, monospace' }}
          />
        </div>
      );
    }

    return (
      <Editor
        key={editorRenderKey}
        height="100%"
        defaultLanguage="json"
        value={jsonValue}
        onChange={(value) => setJsonValue(value ?? '')}
        onMount={handleEditorDidMount}
        theme="vs-light"
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          folding: true,
          bracketPairColorization: { enabled: true },
        }}
      />
    );
  };

  const handleGenerateTemplate = useCallback(() => {
    // Generate template locally with PascalCase and all fields populated
    const json = generateTemplateJson(availableLevelNames, availableTopicNames);
    setJsonValue(json);
  }, [availableLevelNames, availableTopicNames]);

  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      setJsonValue(JSON.stringify(parsed, null, 2));
    } catch {
      message.warning(t('quiz.bulkImport.cannotFormat', 'Cannot format invalid JSON'));
    }
  }, [jsonValue, t]);

  const handleCopyTemplate = useCallback(() => {
    const json = generateTemplateJson(availableLevelNames, availableTopicNames);
    navigator.clipboard.writeText(json);
    message.success(t('quiz.bulkImport.copiedTemplate', 'Template copied to clipboard'));
  }, [availableLevelNames, availableTopicNames, t]);

  const handleClear = useCallback(() => {
    setJsonValue('');
    setSelectedQuestionIndex(null);
  }, []);

  const handleLocateQuestion = useCallback((questionIndex) => {
    setSelectedQuestionIndex(questionIndex);
    
    if (!editorRef.current || !jsonValue) return;

    const lineRange = findQuestionLineRange(jsonValue, questionIndex);
    if (lineRange) {
      const editor = editorRef.current;
      
      // Reveal and select the question in the editor
      editor.revealLineInCenter(lineRange.startLine);
      editor.setSelection({
        startLineNumber: lineRange.startLine,
        startColumn: 1,
        endLineNumber: lineRange.endLine,
        endColumn: 1000,
      });
      editor.focus();
    }
  }, [jsonValue]);

  const handleImport = async () => {
    if (!canImport) return;

    setImporting(true);
    try {
      const result = await bulkAddQuestions(collectionId, parsedQuestions);
      
      if (result.success) {
        message.success(
          t('quiz.bulkImport.success', 'Successfully imported {{count}} questions', { count: result.successCount })
        );
        onSuccess?.();
        onClose();
      } else {
        // Show server-side validation errors
        const errorMessages = result.errors
          ?.map(e => `#${e.index + 1}: ${e.errors.join(', ')}`)
          .join('\n');
        
        Modal.error({
          title: t('quiz.bulkImport.importFailed', 'Import Failed'),
          content: (
            <div style={{ maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {errorMessages || t('quiz.bulkImport.unknownError', 'Unknown error occurred')}
            </div>
          ),
          width: 600,
        });
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Import failed';
      message.error(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  // Render enhanced question card
  const renderQuestionCard = (item, idx) => {
    const isValid = item.valid;
    const question = parsedQuestions[idx];
    const choices = question?.Choices || [];
    const weights = question?.Weights || [];

    return (
      <div 
        key={idx}
        className={`${styles.bulkQuestionCard} ${isValid ? styles.bulkQuestionValid : styles.bulkQuestionInvalid} ${selectedQuestionIndex === idx ? styles.bulkQuestionSelected : ''}`}
        onClick={() => handleLocateQuestion(idx)}
      >
        {/* Header */}
        <div className={styles.bulkQuestionCardHeader}>
          <div className={styles.bulkQuestionCardTitle}>
            <Badge
              count={item.index + 1}
              style={{ backgroundColor: isValid ? '#52c41a' : '#ff4d4f' }}
            />
            {item.code ? (
              <Text strong className={styles.bulkQuestionCodeText}>{item.code}</Text>
            ) : (
              <Tag color="blue" className={styles.bulkQuestionAutoTag}>
                {t('quiz.bulkImport.codeAuto', 'Code: Auto')}
              </Tag>
            )}
          </div>
          <div className={styles.bulkQuestionCardStatus}>
            <Tooltip title={t('quiz.bulkImport.locateInEditor', 'Click to locate in editor')}>
              <Button 
                type="text" 
                size="small" 
                icon={<AimOutlined />} 
                onClick={(e) => { e.stopPropagation(); handleLocateQuestion(idx); }}
              />
            </Tooltip>
            {isValid ? (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            )}
          </div>
        </div>

        {/* Body */}
        <div className={styles.bulkQuestionCardBody}>
          <Paragraph ellipsis={{ rows: 2, expandable: false }} style={{ marginBottom: 0 }}>
            {item.body || <Text type="secondary" italic>{t('quiz.bulkImport.noBody', '(No body text)')}</Text>}
          </Paragraph>
        </div>

        {/* Metadata Row */}
        <div className={styles.bulkQuestionCardMeta}>
          {item.levelOfDifficulty && (
            <Tag color="purple">{item.levelOfDifficulty}</Tag>
          )}
          {item.topic && (
            <Tag color="cyan">{item.topic}</Tag>
          )}
          {item.subtopic && (
            <Tag color="geekblue">{item.subtopic}</Tag>
          )}
          {question?.Law && (
            <Tooltip title={`Law: ${question.Law}`}>
              <Tag color="orange">Law</Tag>
            </Tooltip>
          )}
          {question?.Version && (
            <Tag color="default">v{question.Version}</Tag>
          )}
        </div>

        {/* Choices */}
        <div className={styles.bulkQuestionCardChoices}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
            {t('quiz.bulkImport.choices', 'Choices')} ({choices.length}):
          </Text>
          <div className={styles.bulkChoicesList}>
            {choices.slice(0, 6).map((choice, i) => (
              <div key={i} className={styles.bulkChoiceItem}>
                {choice.IsCorrect ? (
                  <CheckOutlined style={{ color: '#52c41a', marginRight: 4, fontSize: 11 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: '#d9d9d9', marginRight: 4, fontSize: 11 }} />
                )}
                <Text 
                  ellipsis 
                  style={{ 
                    fontSize: 12, 
                    color: choice.IsCorrect ? '#389e0d' : 'inherit',
                    fontWeight: choice.IsCorrect ? 500 : 400
                  }}
                >
                  {choice.Text || <i>(empty)</i>}
                </Text>
              </div>
            ))}
            {choices.length > 6 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                +{choices.length - 6} more...
              </Text>
            )}
          </div>
        </div>

        {/* Weights */}
        {weights.length > 0 && (
          <div className={styles.bulkQuestionCardWeights}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
              {t('quiz.bulkImport.weights', 'Weights')}:
            </Text>
            <Space size={4} wrap>
              {weights.map((w, i) => (
                <Tag key={i} color="green" style={{ fontSize: 11, margin: 0 }}>
                  {w.TopicName}: {(w.Weight * 100).toFixed(0)}%
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {/* Validation Errors */}
        {!isValid && item.errors.length > 0 && (
          <div className={styles.bulkQuestionCardErrors}>
            <Divider style={{ margin: '8px 0' }} />
            <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
              {t('quiz.bulkImport.validationIssues', 'Validation Issues')}:
            </Text>
            <ul className={styles.bulkErrorList}>
              {item.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <DownloadOutlined />
          {t('quiz.bulkImport.title', 'Bulk Import Questions')}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width="95vw"
      style={{ top: 20, maxWidth: 1600 }}
      styles={{ body: { padding: '16px 24px', height: 'calc(100vh - 160px)', overflow: 'hidden' } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            {parsedQuestions.length > 0 && (
              <>
                <Badge count={validCount} style={{ backgroundColor: '#52c41a' }} showZero />
                <Text type="secondary">{t('quiz.bulkImport.valid', 'valid')}</Text>
                {errorCount > 0 && (
                  <>
                    <Badge count={errorCount} style={{ backgroundColor: '#ff4d4f' }} />
                    <Text type="secondary">{t('quiz.bulkImport.withErrors', 'with errors')}</Text>
                  </>
                )}
              </>
            )}
          </Space>
          <Space>
            <Button onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={handleImport}
              disabled={!canImport}
              loading={importing}
              icon={<DownloadOutlined />}
            >
              {importing
                ? t('quiz.bulkImport.importing', 'Importing...')
                : t('quiz.bulkImport.importButton', 'Import {{count}} Questions', { count: parsedQuestions.length })
              }
            </Button>
          </Space>
        </div>
      }
    >
      <div className={styles.bulkImportLayout}>
        {/* Left Panel - Editor */}
        <div className={styles.bulkImportEditor}>
          <div className={styles.bulkImportToolbar}>
            <Space size="small">
              <Tooltip title={t('quiz.bulkImport.generateTemplateTooltip', 'Insert example JSON template')}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleGenerateTemplate}
                  loading={loadingTemplate}
                >
                  {t('quiz.bulkImport.template', 'Template')}
                </Button>
              </Tooltip>
              <Tooltip title={t('quiz.bulkImport.copyTemplateTooltip', 'Copy template to clipboard for AI tools')}>
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyTemplate}
                />
              </Tooltip>
              <Tooltip title={t('quiz.bulkImport.formatJsonTooltip', 'Format/prettify JSON')}>
                <Button
                  icon={<FormatPainterOutlined />}
                  onClick={handleFormatJson}
                  disabled={!jsonValue}
                />
              </Tooltip>
              <Tooltip title={t('quiz.bulkImport.clearTooltip', 'Clear editor')}>
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClear}
                  disabled={!jsonValue}
                />
              </Tooltip>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {jsonValue.length.toLocaleString()} {t('quiz.bulkImport.chars', 'chars')}
            </Text>
          </div>

          <div className={styles.bulkImportEditorWrapper}>
            {renderEditor()}
          </div>

          {/* Parse status */}
          {parseError ? (
            <Alert
              type="error"
              showIcon
              icon={<CloseCircleOutlined />}
              message={t('quiz.bulkImport.invalidJson', 'Invalid JSON')}
              description={parseError}
              className={styles.bulkImportStatus}
            />
          ) : parsedQuestions.length > 0 ? (
            <Alert
              type={errorCount > 0 ? 'warning' : 'success'}
              showIcon
              icon={errorCount > 0 ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
              message={
                errorCount > 0
                  ? t('quiz.bulkImport.validationErrors', '{{count}} question(s) have errors', { count: errorCount })
                  : t('quiz.bulkImport.allValid', 'All questions valid!')
              }
              description={t('quiz.bulkImport.questionsFound', '{{count}} question(s) ready for import', { count: parsedQuestions.length })}
              className={styles.bulkImportStatus}
            />
          ) : null}
        </div>

        {/* Right Panel - Preview */}
        <div className={styles.bulkImportPreview}>
          <Collapse
            defaultActiveKey={parsedQuestions.length === 0 ? ['instructions'] : []}
            ghost
            size="small"
            items={[
              {
                key: 'instructions',
                label: (
                  <Space>
                    <BulbOutlined />
                    <Text strong>{t('quiz.bulkImport.instructionsTitle', 'Instructions & Schema')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.bulkInstructions}>
                    <Paragraph style={{ marginBottom: 8 }}>
                      {t('quiz.bulkImport.instructionsIntro', 'Paste a JSON array of question objects with these fields:')}
                    </Paragraph>
                    
                    <div className={styles.bulkSchemaGrid}>
                      <div><Text code>Body</Text></div>
                      <div>{t('quiz.bulkImport.schemaBody', 'Question text (required, max 5000 chars)')}</div>
                      <div><Text code>Code</Text></div>
                      <div>{t('quiz.bulkImport.schemaCode', 'Question code (optional, auto-generated if null)')}</div>
                      <div><Text code>LevelOfDifficulty</Text></div>
                      <div>{t('quiz.bulkImport.schemaLevel', 'Difficulty name (optional)')}</div>
                      <div><Text code>Topic</Text>, <Text code>Subtopic</Text></div>
                      <div>{t('quiz.bulkImport.schemaTopic', 'Free-text labels (optional)')}</div>
                      <div><Text code>Choices</Text></div>
                      <div>{t('quiz.bulkImport.schemaChoices', 'Array of {Text, IsCorrect} (min 2, at least 1 correct)')}</div>
                      <div><Text code>Weights</Text></div>
                      <div>{t('quiz.bulkImport.schemaWeights', 'Array of {TopicName, Weight} (optional, 0-1)')}</div>
                    </div>

                    {availableLevelNames.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text strong style={{ fontSize: 12 }}>{t('quiz.bulkImport.availableLevels', 'Levels:')} </Text>
                        {availableLevelNames.map(level => (
                          <Tag key={level} color="purple" style={{ fontSize: 11 }}>{level}</Tag>
                        ))}
                      </div>
                    )}

                    {availableTopicNames.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Text strong style={{ fontSize: 12 }}>{t('quiz.bulkImport.availableTopics', 'Topics:')} </Text>
                        {availableTopicNames.map(topic => (
                          <Tag key={topic} color="green" style={{ fontSize: 11 }}>{topic}</Tag>
                        ))}
                      </div>
                    )}

                    <Alert
                      type="info"
                      icon={<InfoCircleOutlined />}
                      showIcon
                      message={t('quiz.bulkImport.aiTip', 'AI Tip')}
                      description={t(
                        'quiz.bulkImport.aiTipDescription',
                        'Click "Template" for example JSON, then copy and share with ChatGPT or Claude to generate questions!'
                      )}
                      style={{ marginTop: 12 }}
                    />
                  </div>
                ),
              },
            ]}
          />

          {/* Question preview cards */}
          {validationResults.length > 0 && (
            <div className={styles.bulkQuestionList}>
              <div className={styles.bulkPreviewHeader}>
                <QuestionCircleOutlined />
                <Text strong>{t('quiz.bulkImport.previewTitle', 'Question Preview')}</Text>
                <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  {t('quiz.bulkImport.clickToLocate', 'Click card to locate in editor')}
                </Text>
              </div>
              <div className={styles.bulkQuestionCards}>
                {validationResults.map((item, idx) => renderQuestionCard(item, idx))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {parsedQuestions.length === 0 && !parseError && (
            <div className={styles.bulkEmptyState}>
              <QuestionCircleOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
              <Text type="secondary">
                {t('quiz.bulkImport.emptyState', 'Paste JSON or click Template to get started')}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BulkImportModal;
